import React, { useState, useEffect } from "react";
import {
  Route,
  Switch,
  Redirect,
  useLocation as usePath,
  useParams
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import {
  administrationGroup,
  administrationGroups,
  administrationGroupInfo,
  administrationGroupAssignments,
  administrationGroupAddAssignment,
  administrationGroupManagePositions,
  buildGroupInformationRoute,
  buildGroupAssignmentsRoute,
  buildAddGroupAssignmentRoute,
  administrationGroupManageLocations,
  buildGroupManageMembersRoute,
  buildGroupDetailLeadersRoute
} from "../../routes/AdminRoutes";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import Tabs from "../../components/Tabs/Tabs";
import GroupInformation from "../../views/Groups/GroupInformation";
import GroupLeaders from "../../views/Groups/GroupLeaders";
import { useGroup } from "../../hooks/groups";
import { Box } from "@mui/material";
import AddAssignment from "../../views/Assignments/AddAssignment";
import { useReport } from "../../hooks/useReport";
import AssignmentsTable from "../../views/Assignments/AssignmentsTable";
import ManageGroupPositions from "../../views/Groups/ManageGroupPositions";
import ManageGroupLocations from "../../views/Groups/ManageGroupLocations";
import ManageGroupMembers from "../../views/Groups/ManageGroupMembers";
import {
  enqueueSuccessSnackbar,
  enqueueErrorSnackbar
} from "../../redux/actions/snackbarActions";
import { useGroups } from "../../hooks/apiHooks/groups";
import usePermissions from "../../redux/selectors/usePermissions";
import PrivateRoute from "../../components/PrivateRoute/PrivateRoute";

const AdminGroupDetails = ({
  groupId,
  group,
  isLoading,
  onAddAssignmentClick,
  onEditGroup,
  onDeleteGroup,
  isDeletingGroup
}) => {
  const currentPath = usePath();
  const { isGoldOrPlatinum } = usePermissions();
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [currentTab, setCurrentTab] = useState(0);

  const handleGoBack = () => {
    dispatch(push(administrationGroups));
  };

  const openPrintReport = useReport("Training Assignment by Group Report", {
    filters: {
      group_name: group?.name ? `${groupId}|${group.name}` : null
    },
    invoke: "print"
  });

  const openMailReport = useReport("Training Assignment by Group Report", {
    filters: {
      group_name: group?.name ? `${groupId}|${group.name}` : null
    },
    invoke: "email"
  });

  const tabs = [
    {
      label: t("administration.tabs.groupInfoTab"),
      path: buildGroupInformationRoute(groupId),
      disabled: false
    },
    {
      label: t("administration.tabs.assignmentsTab"),
      path: buildGroupAssignmentsRoute(groupId),
      disabled: !isGoldOrPlatinum,
      tooltip: !isGoldOrPlatinum
        ? t("administration.goldPlatinumFeatureTooltip")
        : ""
    }
  ];

  useEffect(() => {
    const currentPathIndex = tabs.findIndex(
      tab => tab.path === currentPath.pathname
    );
    if (currentPathIndex !== -1) {
      setCurrentTab(currentPathIndex);
    }
  }, [currentPath]);

  const handleTabChange = newTab => {
    setCurrentTab(newTab);
    dispatch(push(tabs[newTab].path));
  };

  const handleDeleteGroup = groupId => {
    onDeleteGroup(groupId).then(() => {
      handleGoBack();
    });
  };

  return (
    <AdminPageLayout
      title={<Breadcrumb routes={["Groups", group ? group.name : ""]} />}
      onGoBackClick={handleGoBack}
    >
      <Tabs
        tabs={tabs}
        currentTab={currentTab}
        onChange={newTab => handleTabChange(newTab)}
        scrollButtons={false}
        textVariant="h6"
      />
      <Box my={3}>
        <Route
          exact
          path={administrationGroup}
          render={() => {
            return <Redirect to={buildGroupInformationRoute(groupId)} />;
          }}
        />
        <Route
          exact
          path={administrationGroupInfo}
          render={() => {
            return (
              <GroupInformation
                group={group || {}}
                isLoading={isLoading}
                groupId={groupId}
                isDeleting={isDeletingGroup}
                onDeleteGroup={handleDeleteGroup}
                onEditGroup={onEditGroup}
              />
            );
          }}
        />
        <PrivateRoute
          permitted={isGoldOrPlatinum}
          exact
          redirectRoute={administrationGroups}
          path={administrationGroupAssignments}
          render={() => {
            return (
              <AssignmentsTable
                referenceType="group"
                referenceId={groupId}
                onAddAssignmentClick={onAddAssignmentClick}
                onOpenMailReport={openMailReport}
                onOpenPrintReport={openPrintReport}
              />
            );
          }}
        />
      </Box>
    </AdminPageLayout>
  );
};

const AdminGroup = () => {
  const { t } = useTranslation();

  const { groupId } = useParams();

  const {
    group,
    fetchGroup,
    deleteGroup,
    batchUpdateGroupPositions,
    batchUpdateGroupLocations,
    isLoading,
    isDeleting
  } = useGroup(groupId);

  const { updateGroup } = useGroups();

  const dispatch = useDispatch();

  const handleAddAssignmentClick = () => {
    dispatch(push(buildAddGroupAssignmentRoute(groupId)));
  };

  const handleGoToAssignmentsView = () => {
    dispatch(push(buildGroupAssignmentsRoute(groupId)));
  };

  const handleEditGroup = async data => {
    await updateGroup(group.id, {
      action: "update_group",
      name: data.name
    }).then(response => {
      if (response.errors) {
        dispatch(
          enqueueErrorSnackbar({
            message: t("groupsFilters.updateErrorMessage")
          })
        );
      } else {
        fetchGroup();
        dispatch(
          enqueueSuccessSnackbar({
            message: t("groupsFilters.updateSuccessMessage")
          })
        );
      }
    });
  };

  const handleSaveGroupPositions = async (
    positionsToCreate,
    positionsToDelete
  ) => {
    // Map deleted position ids to the related groupsPositions
    const groupsPositionsToDelete = positionsToDelete.map(
      ({ position: { id: positionToDeleteId } }) => ({
        type: "groupsPosition",
        id: group.groupsPositions.find(
          ({ position }) => position.id === positionToDeleteId
        )?.id
      })
    );
    const response = await batchUpdateGroupPositions(
      positionsToCreate,
      groupsPositionsToDelete
    );
    fetchGroup();
    return response;
  };

  const handleSaveGroupLocations = async (
    locationsToCreate,
    locationsToDelete
  ) => {
    // Map deleted location ids to the related groupsLocations
    const groupsLocationsToDelete = locationsToDelete.map(
      ({ location: { id: locationToDeleteId } }) => ({
        type: "groupsLocation",
        id: group.groupsLocations.find(
          ({ location }) => location.id === locationToDeleteId
        )?.id
      })
    );
    const response = await batchUpdateGroupLocations(
      locationsToCreate,
      groupsLocationsToDelete
    );
    fetchGroup();
    return response;
  };

  const handleGoToInformationView = () => {
    dispatch(push(buildGroupInformationRoute(groupId)));
  };

  return (
    <Switch>
      <Route
        exact
        path={buildGroupDetailLeadersRoute(groupId)}
        render={props => {
          return (
            <GroupLeaders group={group} goBack={handleGoToInformationView} />
          );
        }}
      />
      <Route
        exact
        path={buildGroupManageMembersRoute(groupId)}
        render={props => {
          return (
            <ManageGroupMembers
              group={group}
              goBack={handleGoToInformationView}
            />
          );
        }}
      />
      <Route
        exact
        path={administrationGroupAddAssignment}
        render={() => {
          return (
            <AddAssignment
              referenceType="group"
              referenceId={groupId}
              referenceName={group ? group.name : ""}
              onGoBackClick={handleGoToAssignmentsView}
            />
          );
        }}
      />
      <Route
        exact
        path={administrationGroupManagePositions}
        render={() => {
          return (
            <ManageGroupPositions
              groupPositions={group ? group.positions : []}
              groupName={group?.name}
              groupId={group?.id}
              divisionId={group?.customer?.id}
              onSave={handleSaveGroupPositions}
            />
          );
        }}
      />
      <Route
        exact
        path={administrationGroupManageLocations}
        render={() => {
          return (
            <ManageGroupLocations
              groupLocations={group ? group.locations : []}
              groupName={group?.name}
              groupId={group?.id}
              divisionId={group?.customer?.id}
              onSave={handleSaveGroupLocations}
            />
          );
        }}
      />
      <Route
        path={[administrationGroupInfo, administrationGroupAssignments]}
        render={() => (
          <AdminGroupDetails
            groupId={groupId}
            isLoading={isLoading}
            group={group}
            onAddAssignmentClick={handleAddAssignmentClick}
            onDeleteGroup={deleteGroup}
            onEditGroup={handleEditGroup}
            isDeletingGroup={isDeleting}
          />
        )}
      />
    </Switch>
  );
};

export default withRouter(AdminGroup);
